import { useCallback, useEffect, useState } from "react";
import type { ReactElement } from "react";
import { Modal, ModalFuss, darfAbbrechen, useBestaetigung, useFormularZustand }
  from "@kundeninfo/ui";
import { Formular, Hinweis, Schalter } from "@kundeninfo/ui/me-design";
import type {
  AircallAnruf, AircallKandidat, AircallKlaerGruppe, AircallKlaerliste, AircallStatus,
  ApiClient, KundeListe, Unternehmen,
} from "@kundeninfo/api-client";
import { KundenListe } from "./KundenListe.js";
import { formatZeitpunkt } from "../format.js";
import "./AircallDienst.css";
import { KiKnopf, Suchfeld } from "../components/meHuellen.js";

/**
 * Der AIRCALL-DIENST im Bereich „Schnittstellen“ (Spec „Aircall-Abrechnungsadapter“,
 * Stufe 1, Entscheid vom 11.09.2026) — Zustand, Kennzahlen und die KLÄRLISTE.
 *
 * **Kein eigener Menüpunkt.** Die Spec sagt es wörtlich: Der Aircall-Dienst erscheint als
 * Eintrag in der Dienste-Spalte, und sein Detail trägt drei Dinge — den Zustand (letzter
 * Lauf, Rückstand), die Kennzahlen des Bestands (Anrufe gesamt / ungeklärt) und die
 * Klärliste selbst. Diese Datei ist dieses Detail; die Zeile in der Spalte zeichnet
 * views/Schnittstellen.tsx und holt sich Beschriftung und Ampelfarbe aus `aircallZustand`
 * unten — EINE Quelle für beide Flächen.
 *
 * **Der Zustand kommt von aussen** (`status`): Gemessen wird in Schnittstellen.tsx, und
 * zwar für Spalte und Detail in EINEM Aufruf. Eine zweite Messung hier hiesse, dass Zeile
 * und Karte für denselben Augenblick verschiedene Zahlen zeigen könnten — auf einer
 * Statusseite die schlimmste Sorte Widerspruch.
 *
 * **Die Klärliste holt dieser Baustein dagegen selbst.** Sie ist kein Status, sondern eine
 * Arbeitsliste mit eigener Sicht (offen/alle), eigener Suche und eigener Blätterung; sie
 * gehört dorthin, wo diese drei Angaben stehen. Der Aktualisieren-Knopf der Spalte misst
 * deshalb den ZUSTAND neu, nicht die Liste — die lädt nach jeder Klärung von selbst nach.
 *
 * **Der CSS-Block heisst `ac` und gehört ALLEIN dieser Datei** (Hausregel: kein geteilter
 * Blockname über Ansichten hinweg, Lektion aus `.ka__`). Die zwei Ampel-Regeln stehen
 * darin ein zweites Mal statt als geteilte `schn`-Klasse — dieselbe Begründung, mit der
 * Schnittstellen.css die Optik des Verwaltungs-Untermenüs abschreibt statt sie zu leihen:
 * Eine Anpassung dort soll diese Karte nicht still mitverändern.
 */

/** Die Wege, die dieser Baustein braucht — als `Pick`, damit der Test ihn mit vier `vi.fn`
 *  zeichnen kann (Hausmuster: SchnittstellenClient, BereichArtikelClient). */
export type AircallClient = Pick<
  ApiClient,
  "aircallKlaerliste" | "aircallZuordnen" | "aircallIgnorieren" | "getCustomers"
>;

export type AircallDienstProps = {
  client: AircallClient;
  /** Die gemeinsame Messung aus Schnittstellen.tsx. `null` heisst „läuft noch“ oder
   *  „gescheitert“ — welches von beidem, sagt `statusFehler`. */
  status: AircallStatus | null;
  statusFehler: boolean;
  /**
   * Nach einer Klärung den ZUSTAND neu messen lassen: „ungeklärt“ ist danach eine andere
   * Zahl. Ohne diesen Rückruf stünde über einer Liste, aus der gerade fünf Anrufe
   * verschwunden sind, unverändert die alte Kennzahl — und der Bearbeiter hielte die
   * Messung für kaputt.
   */
  onStatusNeu: () => void;
  /** Für die Kürzel-Chips der Kundensuche. Ohne Angabe erscheinen sie neutral. */
  companies?: Unternehmen[];
};

/* ------------------------------------------------------------------------------------ */
/* Der Zustand der Zeile — die EINE Ableitung für Spalte und Karte                        */
/* ------------------------------------------------------------------------------------ */

/** Die Ampelfarbe als Klassen-Endung; die Farbwerte selbst stehen in den Tokens. */
export type AircallKlasse = "ok" | "schlecht" | "warn" | "laeuft" | "aus";

export type AircallZeilenZustand = {
  label: string;
  klasse: AircallKlasse;
  /** Der Klartext für die Karte — er sagt, was IST und was zu tun wäre. */
  erklaerung: string;
};

/** Ab hier ist der Rückstand rot (Spec „Verwaltung und Sichtbarkeit“: „rot ab sieben Tagen
 *  Rückstand"). Als Konstante, weil der Test genau diese Schwelle misst. */
export const RUECKSTAND_ROT_TAGE = 7;

/** Der Name des Dienstes — in der Spalte, in der Karte und im zugänglichen Namen des
 *  Eintrags derselbe. */
export const AIRCALL_NAME = "Aircall-Anrufübernahme";

/**
 * Aus dem Status der Route wird die Zeile — die Reihenfolge der Fälle IST die Hausregel
 * „Schlechtestes oben“ (ERROR > UNKNOWN > WARN > OK), nur an einer einzelnen Zeile
 * angewandt: Trifft mehreres zu, gewinnt die schlechtere Auskunft.
 *
 * `null` + `statusFehler` heisst UNBEKANNT und ist ausdrücklich nicht „nicht
 * konfiguriert": Der Weg zur eigenen API hat nicht geantwortet: Über Aircall ist damit
 * NICHTS gesagt. Ein grauer Punkt („nicht eingerichtet“) wäre an dieser Stelle eine
 * Behauptung über ein fremdes System, die aus einem Fehler im eigenen Haus stammt.
 */
export function aircallZustand(
  status: AircallStatus | null, statusFehler: boolean,
): AircallZeilenZustand {
  if (statusFehler) {
    return {
      label: "Nicht abfragbar", klasse: "warn",
      erklaerung: "Der Stand der Anruf-Übernahme konnte nicht abgefragt werden. Über "
        + "Aircall selbst ist damit nichts gesagt — bitte in der Spalte noch einmal "
        + "aktualisieren.",
    };
  }
  if (status === null) {
    return { label: "Wird gemessen", klasse: "aus", erklaerung: "Der Stand wird geholt …" };
  }
  if (!status.configured) {
    return {
      label: "Nicht konfiguriert", klasse: "aus",
      erklaerung: "Für dieses System ist kein Aircall-Konto hinterlegt. Es wird nichts "
        + "geholt, und es gibt nichts zu klären. Einrichten kann das nur, wer an die "
        + "Konfiguration des Dienstes kommt.",
    };
  }
  if (status.rueckstandTage !== null && status.rueckstandTage >= RUECKSTAND_ROT_TAGE) {
    return {
      label: "Rückstand", klasse: "schlecht",
      erklaerung: `Der letzte erfolgreich geholte Anruf liegt ${status.rueckstandTage} Tage `
        + "zurück. Ein unbemerkter Dauerausfall ist das einzige wirklich gefährliche "
        + "Fehlerbild dieser Anbindung — bitte den Läufer `aircall-pull` und sein "
        + "Protokoll prüfen lassen.",
    };
  }
  if (status.lastRunStatus === "ERROR") {
    return {
      label: "Letzter Lauf gescheitert", klasse: "warn",
      erklaerung: "Der letzte Lauf ist abgebrochen. Der Cursor steht deshalb still, und "
        + "der nächste Lauf holt das grössere Fenster nach — verschwindet die Meldung "
        + "morgen nicht, gehört sie geprüft. Den Grund nennt das Protokoll des Läufers.",
    };
  }
  if (status.rueckstandTage === null) {
    return {
      label: "Noch kein Lauf", klasse: "warn",
      erklaerung: "Die Zugangsdaten stehen, aber es ist noch nie ein Lauf durchgekommen — "
        + "es liegt also noch kein Bestand vor. Solange das so bleibt, ist hier nichts zu "
        + "klären.",
    };
  }
  if (status.lastRunStatus === "RUNNING") {
    return {
      label: "Lauf läuft", klasse: "laeuft",
      erklaerung: "Gerade holt ein Lauf die Anrufe. Die Zahlen darunter sind der Stand "
        + "von eben; nach dem Lauf sagt ein Aktualisieren mehr.",
    };
  }
  return {
    label: "Aktuell", klasse: "ok",
    erklaerung: "Die Anruf-Übernahme läuft. Was hier unten offen steht, wartet auf eine "
      + "Entscheidung von Hand — alles andere ist zugeordnet oder weggeklärt.",
  };
}

/* ------------------------------------------------------------------------------------ */
/* Kleine Formate                                                                         */
/* ------------------------------------------------------------------------------------ */

/** Sekunden als Dauer. `null` heisst „gibt es nicht“ (ein nicht angenommener Anruf hat
 *  keine Gesprächszeit) — und dann steht ein Strich da und keine 0. */
export function formatDauer(sekunden: number | null): string {
  if (sekunden === null) return "—";
  if (sekunden < 60) return `${sekunden} s`;
  const minuten = Math.floor(sekunden / 60);
  const rest = sekunden % 60;
  return `${minuten}:${String(rest).padStart(2, "0")} min`;
}

const RICHTUNG: Record<AircallAnruf["direction"], string> = {
  INBOUND: "eingehend",
  OUTBOUND: "ausgehend",
};

/** Der Stand eines einzelnen Anrufs — in der Vorgabesicht immer „offen“, in der
 *  Korrektursicht der Grund, warum eine Gruppe GEMISCHT heisst. */
const ANRUF_STAND: Record<AircallAnruf["matchStatus"], string> = {
  MATCHED: "zugeordnet",
  UNRESOLVED: "offen",
  IGNORED: "ignoriert",
};

const GRUPPEN_STAND: Record<AircallKlaerGruppe["zustand"], string> = {
  OFFEN: "offen",
  GEMISCHT: "teils entschieden",
  ZUGEORDNET: "zugeordnet",
  IGNORIERT: "ignoriert",
};

const LAUF_ERGEBNIS: Record<NonNullable<AircallStatus["lastRunStatus"]>, string> = {
  OK: "erfolgreich",
  ERROR: "gescheitert",
  RUNNING: "läuft gerade",
};

/**
 * Hat diese Gruppe noch OFFENE Anrufe? Nur dann ist „Ignorieren“ ein Weg, der etwas tut
 * (Empfehlung aus dem T4-Bericht, Bedenken 1): Der Schreibweg fasst ausschliesslich offene
 * Anrufe an und antwortet sonst mit 404 — ein Knopf, der nur 404 liefern kann, ist kein
 * Angebot, sondern eine Falle.
 *
 * `zustand === "OFFEN"` allein reicht als Prüfung NICHT: `GEMISCHT` heisst „zwei Sorten in
 * einer Gruppe“ und kann auch „zugeordnet + ignoriert“ sein, ganz ohne offenen Anruf.
 * Deshalb die zweite Frage an die mitgelieferten Anrufe.
 *
 * Bewusst konservativ: Die Antwort trägt nur die 20 jüngsten Anrufe je Gruppe. Liegt der
 * einzige offene Anruf einer sehr grossen Gruppe weiter hinten, fehlt der Knopf, obwohl er
 * gewirkt hätte. Lieber ein Angebot zu wenig als eines, das mit einem Fehler endet — und
 * die Gruppe ist dann ohnehin über „Zuordnen“ zu klären.
 */
export function hatOffeneAnrufe(gruppe: AircallKlaerGruppe): boolean {
  if (gruppe.zustand === "OFFEN") return true;
  return gruppe.anrufe.some((a) => a.matchStatus === "UNRESOLVED");
}

/* ------------------------------------------------------------------------------------ */
/* Die Quittung                                                                           */
/* ------------------------------------------------------------------------------------ */

/**
 * Die Quittung einer Klärung — MIT der Nummer, zu der sie gehört.
 *
 * Sie trägt ihre Kennung im Text und nicht nur im Zustand: Eine Quittung, die „5 Anrufe
 * zugeordnet" sagt, während die Liste darunter längst eine andere Sicht zeigt, gehört
 * sichtbar zu der Nummer, an der sie entstanden ist (dieselbe Lektion wie bei der Quittung
 * über dem Artikel, Fixrunde 1 der Bereichs-Spalten).
 */
type Quittung = { nummer: string; text: string };

/* ------------------------------------------------------------------------------------ */
/* Der Baustein                                                                           */
/* ------------------------------------------------------------------------------------ */

const JE_SEITE = 25;

export function AircallDienst(p: AircallDienstProps): ReactElement {
  const zustand = aircallZustand(p.status, p.statusFehler);
  const eingerichtet = p.status !== null && p.status.configured;

  return (
    <div className="ac">
      <article className="ac__karte">
        <div className="ac__kopfzeile">
          <span className={`ac__punkt ac__punkt--${zustand.klasse}`} aria-hidden="true" />
          <h2 className="ac__titel">{AIRCALL_NAME}</h2>
          <span className={`ac__zustand ac__zustand--${zustand.klasse}`}>
            {zustand.label}
          </span>
        </div>
        <p className="ac__erklaerung">{zustand.erklaerung}</p>
        {p.status !== null && p.status.configured && <Kennzahlen status={p.status} />}
      </article>

      {/*
        * Die Klärliste gibt es nur bei eingerichtetem Dienst — und zwar OHNE dass sie
        * dafür erst eine leere Antwort holen müsste. Ohne Zugangsdaten ist die ehrliche
        * Auskunft die Karte darüber („kein Aircall-Konto hinterlegt“); eine Liste mit
        * „Nichts zu klären“ behauptete an derselben Stelle, es sei alles erledigt.
        */}
      {eingerichtet && <Klaerliste
        client={p.client}
        onStatusNeu={p.onStatusNeu}
        {...(p.companies === undefined ? {} : { companies: p.companies })}
      />}
    </div>
  );
}

/** Die Kennzahlen des Bestands — als Beschreibungsliste, weil es Paare aus Frage und
 *  Antwort sind und kein Fliesstext. */
function Kennzahlen({ status }: { status: AircallStatus }): ReactElement {
  const lauf = status.lastRunAt === null
    ? "noch keiner"
    : `${formatZeitpunkt(status.lastRunAt)}`
      + (status.lastRunStatus === null ? "" : ` (${LAUF_ERGEBNIS[status.lastRunStatus]})`);
  // `rueckstandTage: null` ist NICHT 0 (T4-Vertrag): „noch nie erfolgreich gelaufen“ gegen
  // „aktuell“. Zwei Auskünfte, die dieselbe Zahl 0 zusammenwürfe.
  const rueckstand = status.rueckstandTage === null
    ? "noch kein erfolgreicher Lauf"
    : status.rueckstandTage === 0
      ? "aktuell"
      : `${status.rueckstandTage} ${status.rueckstandTage === 1 ? "Tag" : "Tage"}`;

  return (
    <dl className="ac__kennzahlen">
      <div className="ac__kennzahl">
        <dt>Letzter Lauf</dt><dd>{lauf}</dd>
      </div>
      <div className="ac__kennzahl">
        <dt>Anrufe geholt bis</dt>
        <dd>{status.lastPulledUntil === null
          ? "—" : formatZeitpunkt(status.lastPulledUntil)}</dd>
      </div>
      <div className="ac__kennzahl">
        <dt>Rückstand</dt><dd>{rueckstand}</dd>
      </div>
      <div className="ac__kennzahl">
        <dt>Anrufe gesamt</dt><dd>{status.anrufeGesamt}</dd>
      </div>
      <div className="ac__kennzahl">
        <dt>Davon ungeklärt</dt><dd>{status.anrufeUngeklaert}</dd>
      </div>
    </dl>
  );
}

/* ------------------------------------------------------------------------------------ */
/* Die Klärliste                                                                          */
/* ------------------------------------------------------------------------------------ */

type KlaerlisteProps = {
  client: AircallClient;
  onStatusNeu: () => void;
  companies?: Unternehmen[];
};

function Klaerliste(p: KlaerlisteProps): ReactElement {
  const [alle, setAlle] = useState(false);
  /** Der ABGESCHICKTE Suchbegriff — nicht der getippte. Siehe `eingabe` unten. */
  const [suche, setSuche] = useState("");
  const [eingabe, setEingabe] = useState("");
  const [offset, setOffset] = useState(0);
  const [seite, setSeite] = useState<AircallKlaerliste | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [quittung, setQuittung] = useState<Quittung | null>(null);
  const [offen, setOffen] = useState<AircallKlaerGruppe | null>(null);
  const { bestaetigen, bestaetigung } = useBestaetigung();

  /** Zählt hoch, wenn die Liste NACH einer Klärung neu zu holen ist. */
  const [version, setVersion] = useState(0);

  const klaerliste = p.client.aircallKlaerliste;

  useEffect(() => {
    let abgebrochen = false;
    setFehler(null);
    klaerliste({
      ...(alle ? { alle: true } : {}),
      ...(suche === "" ? {} : { suche }),
      limit: JE_SEITE, offset,
    })
      .then((antwort) => { if (!abgebrochen) setSeite(antwort); })
      .catch(() => {
        if (abgebrochen) return;
        // Wie beim Dienste-Stand: Der vorige Stand bleibt NICHT stehen. Eine Arbeitsliste,
        // die nach einem Fehlschlag unverändert dasteht, lädt zum Klären von Zeilen ein,
        // die es vielleicht nicht mehr gibt.
        setSeite(null);
        setFehler("Die Klärliste konnte nicht geladen werden. Bitte noch einmal versuchen.");
      });
    return () => { abgebrochen = true; };
  }, [klaerliste, alle, suche, offset, version]);

  /** Eine Sicht-Änderung fängt vorn an — sonst stünde man auf Seite 7 eines Ergebnisses
   *  mit einer Seite und sähe nichts (Hausmuster der Bereichslisten). */
  const sichtWechseln = useCallback((setzen: () => void): void => {
    setzen();
    setOffset(0);
    // Die Quittung gehört zu der Nummer, an der sie entstanden ist — und die steht in einer
    // ANDEREN Sicht vielleicht gar nicht mehr auf dem Bildschirm.
    setQuittung(null);
  }, []);

  function nachKlaerung(neu: Quittung): void {
    setQuittung(neu);
    setVersion((v) => v + 1);
    // Die Kennzahlen oben („davon ungeklärt“) sind nach einer Klärung andere.
    p.onStatusNeu();
  }

  async function ignorieren(gruppe: AircallKlaerGruppe): Promise<void> {
    // Bestätigungsfrage (striktes Modal, Aufgabe 8): Ignorieren ist der einzige Weg dieser
    // Maske, der Anrufe der Abrechnung ENTZIEHT.
    if (!(await bestaetigen({
      titel: `Nummer ${gruppe.rawNumber} ignorieren?`,
      text: `Alle offenen Anrufe der Nummer ${gruppe.rawNumber} werden als „ignoriert“ `
        + "weggeklärt und damit nicht abgerechnet. Zurücknehmen lässt sich das nur, indem "
        + "die Nummer später einem Kunden zugeordnet wird.",
      bestaetigenText: "Ignorieren",
      gefaehrlich: true,
    }))) return;
    try {
      const ergebnis = await p.client.aircallIgnorieren(gruppe.nummer);
      nachKlaerung({
        nummer: gruppe.nummer,
        text: `Nummer ${gruppe.rawNumber}: ${ergebnis.anrufe} `
          + `${ergebnis.anrufe === 1 ? "Anruf" : "Anrufe"} ignoriert.`,
      });
    } catch (e) {
      setFehler(ignorierenFehler(e));
    }
  }

  const gedeckelt = seite?.gedeckelt === true;
  const items = seite?.items ?? [];
  const von = seite === null || items.length === 0 ? 0 : offset + 1;
  const bis = offset + items.length;

  return (
    <section className="ac__klaerliste" aria-label="Klärliste">
      <div className="ac__leiste">
        <div className="ac__sicht" role="group" aria-label="Sicht">
          {/*
            * Die Vorgabe bleibt die ARBEITSLISTE. „Alle Nummern“ ist die Korrektursicht —
            * und zwar eine SUCHSICHT: Ohne Suchbegriff steht dort der gedeckelte Stand
            * (die Gruppen mit dem jüngsten Anruf), was für das Finden einer bestimmten
            * falsch zugeordneten Nummer nicht reicht. Deshalb stehen Umschalter und
            * Suchfeld nebeneinander und nicht an zwei Enden der Maske.
            */}
          <KiKnopf gedrueckt={!alle} onClick={() => sichtWechseln(() => setAlle(false))}>
            Nur offene
          </KiKnopf>
          <KiKnopf gedrueckt={alle} onClick={() => sichtWechseln(() => setAlle(true))}>
            Alle Nummern
          </KiKnopf>
        </div>

        {/*
          * Die Suche geht auf ABSCHICKEN und nicht auf jeden Tastendruck. Zwei Gründe, und
          * der zweite ist der wichtigere: Eine Nummer, die zeichenweise als Abfrage
          * hinausgeht, erzeugt sieben Anfragen für eine Eingabe — und Telefonnummern sind
          * personenbezogen (die API schneidet sie deshalb aus ihrem Protokoll, siehe
          * api/src/protokoll.ts). Weniger Anfragen heisst hier auch: weniger Gelegenheit.
          */}
        {/* Kein <form> mehr: me-eingabe sendet bei Enter ein umschließendes Formular ab, und
            me-knopf löst kein „submit“ aus. Enter im Suchfeld und „Suchen“ rufen dasselbe. */}
        <div className="ac__suche" role="search">
          <Suchfeld name="Nummer suchen" platzhalter="Nummer suchen" className="ac__suchfeld"
            wert={eingabe} onText={setEingabe}
            onEingabetaste={() => sichtWechseln(() => setSuche(eingabe.trim()))} />
          <KiKnopf onClick={() => sichtWechseln(() => setSuche(eingabe.trim()))}>Suchen</KiKnopf>
          {suche !== "" && (
            <KiKnopf onClick={() => sichtWechseln(() => {
              setSuche(""); setEingabe("");
            })}>
              Suche zurücksetzen
            </KiKnopf>
          )}
        </div>
      </div>

      {/* Quittung und Fehler als me-hinweis (ME-Etappe 3, Aufgabe 7): Rolle `status` bzw.
          `alert` kommt vom Element. */}
      {quittung !== null && <Hinweis art="ok">{quittung.text}</Hinweis>}
      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}

      {fehler === null && seite === null && (
        <p className="ac__zusatz" role="status">Lädt …</p>
      )}

      {seite !== null && items.length === 0 && (
        <p className="ac__leer">
          {suche !== ""
            ? "Zu dieser Nummer steht hier nichts."
            : alle
              ? "Für dieses Unternehmen sind noch keine Anrufe gespeichert."
              : "Nichts zu klären — jeder Anruf ist zugeordnet oder weggeklärt."}
        </p>
      )}

      {/*
        * Der GEDECKELTE Stand, ehrlich benannt (T4-Vertrag `gedeckelt`): Der Server lädt
        * höchstens 500 Gruppen — die mit dem jüngsten Anruf — und sagt, wenn es ältere
        * gibt. Die Suche schränkt VOR dem Deckel ein und findet deshalb auch die ältesten.
        * Ohne diesen Satz sähe die Liste vollständig aus und wäre es nicht.
        */}
      {gedeckelt && (
        <p className="ac__zusatz">
          Hier stehen die {seite?.total} Nummern mit dem jüngsten Anruf; ältere gibt es
          ebenfalls — weitere über die Suche.
        </p>
      )}

      <ul className="ac__gruppen">
        {items.map((g) => (
          <li key={g.nummer} className="ac__gruppe">
            <div className="ac__gruppe-kopf">
              <span className="ac__nummer">{g.rawNumber}</span>
              {g.mehrdeutig && (
                <span className="ac__marke ac__marke--mehrdeutig">mehrdeutig</span>
              )}
              {g.zustand !== "OFFEN" && (
                <span className="ac__marke">{GRUPPEN_STAND[g.zustand]}</span>
              )}
            </div>
            <p className="ac__zusatz">
              {g.anzahl} {g.anzahl === 1 ? "Anruf" : "Anrufe"}
              {" · jüngster "}{formatZeitpunkt(g.juengsterAnruf)}
              {g.kandidaten.length > 0 && (
                <> {" · Vorschläge: "}{g.kandidaten.map((k) => k.name).join(", ")}</>
              )}
            </p>
            {g.zugeordneterKunde !== null && (
              <p className="ac__zusatz">Zugeordnet: {g.zugeordneterKunde.name}</p>
            )}
            <div className="ac__aktionen">
              <KiKnopf art="primaer" name={`Nummer ${g.rawNumber} zuordnen`} onClick={() => { setOffen(g); setFehler(null); }}>
                Zuordnen
              </KiKnopf>
              {hatOffeneAnrufe(g) && (
                <KiKnopf name={`Nummer ${g.rawNumber} ignorieren`} onClick={() => { void ignorieren(g); }}>
                  Ignorieren
                </KiKnopf>
              )}
            </div>
          </li>
        ))}
      </ul>

      {seite !== null && items.length > 0 && (
        <div className="ac__blaetterung">
          <KiKnopf deaktiviert={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - JE_SEITE))}>
            Zurück
          </KiKnopf>
          <span className="ac__zusatz">
            {von}–{bis} von {gedeckelt ? `mindestens ${seite.total}` : seite.total}
          </span>
          <KiKnopf deaktiviert={bis >= seite.total} onClick={() => setOffset((o) => o + JE_SEITE)}>
            Weiter
          </KiKnopf>
        </div>
      )}

      {/*
        * Das strikte Modalpanel am `key` der NUMMER: Jede Gruppe bekommt einen frischen
        * Entwurf. Ohne den Schlüssel trüge das Panel beim zweiten Öffnen die Kundenwahl der
        * vorigen Nummer — die teuerste Sorte Fehlbedienung, weil sie wie eine Vorbelegung
        * aussieht.
        */}
      {offen !== null && (
        <ZuordnenPanel
          key={offen.nummer}
          gruppe={offen}
          client={p.client}
          {...(p.companies === undefined ? {} : { companies: p.companies })}
          onAbbrechen={() => setOffen(null)}
          onFertig={(neu) => { setOffen(null); nachKlaerung(neu); }}
        />
      )}
      {bestaetigung}
    </section>
  );
}

/** Die 404 dieses Weges heisst „zu dieser Nummer ist nichts (mehr) offen“ — sie ist
 *  KEIN Fehler des Bearbeiters, sondern eine überholte Liste. */
function ignorierenFehler(e: unknown): string {
  if (typeof e === "object" && e !== null && (e as { status?: number }).status === 404) {
    return "Zu dieser Nummer ist nichts mehr offen — die Liste war nicht mehr aktuell. "
      + "Bitte die Sicht noch einmal umschalten.";
  }
  return "Das Ignorieren ist nicht durchgekommen. Bitte noch einmal versuchen.";
}

/* ------------------------------------------------------------------------------------ */
/* Das strikte Modalpanel: Zuordnen                                                       */
/* ------------------------------------------------------------------------------------ */

type ZuordnenPanelProps = {
  gruppe: AircallKlaerGruppe;
  client: AircallClient;
  companies?: Unternehmen[];
  onAbbrechen: () => void;
  onFertig: (quittung: Quittung) => void;
};

function ZuordnenPanel(p: ZuordnenPanelProps): ReactElement {
  const { gruppe } = p;
  const [gewaehlt, setGewaehlt] = useState<AircallKandidat | null>(null);
  const [merken, setMerken] = useState(false);
  const [suche, setSuche] = useState("");
  const [treffer, setTreffer] = useState<KundeListe[]>([]);
  const [laedt, setLaedt] = useState(false);
  const [suchFehler, setSuchFehler] = useState<{ message: string } | null>(null);

  const getCustomers = p.client.getCustomers;

  /**
   * Formularzustand des Panels (ME-Etappe 3, Aufgabe 7): `laeuft` sperrt den Merk-Schalter
   * (gesperrt statt disabled, der Fokus bleibt) und beide Ausgänge. Die Fehlerbilder dieses
   * Weges bleiben die eigenen Klartexte (`zuordnenFehler`) — sie gehen als Meldung in den
   * Hook, nicht als Feldbefund: Die 422 NUMMER_NICHT_MERKBAR betrifft den Schalter, den es
   * dann gar nicht gibt, und eine 409 kennt dieser Weg nicht.
   */
  const formular = useFormularZustand<Quittung>({
    speichern: async () => {
      if (gewaehlt === null) throw new Error("Bitte zuerst einen Kunden wählen.");
      try {
        const ergebnis = await p.client.aircallZuordnen({
          nummer: gruppe.nummer, customerId: gewaehlt.customerId, dauerhaftMerken: merken,
        });
        return {
          nummer: gruppe.nummer,
          text: quittungsText(gruppe.rawNumber, gewaehlt.name, ergebnis),
        };
      } catch (e) {
        throw new Error(zuordnenFehler(e));
      }
    },
    neuLaden: async () => { /* keine 409 auf diesem Weg */ },
  });
  const speichert = formular.laeuft;

  useEffect(() => {
    let abgebrochen = false;
    setLaedt(true);
    getCustomers({ ...(suche === "" ? {} : { search: suche }), limit: 20 })
      .then(({ items }) => { if (!abgebrochen) { setTreffer(items); setSuchFehler(null); } })
      .catch(() => {
        if (abgebrochen) return;
        setTreffer([]);
        setSuchFehler({ message: "Die Kundensuche antwortet gerade nicht." });
      })
      .finally(() => { if (!abgebrochen) setLaedt(false); });
    return () => { abgebrochen = true; };
  }, [getCustomers, suche]);

  /** Der eigene Weg hinaus sperrt sich selbst (siehe die Arbeitsteilung am Modal-Baustein);
   *  die Rückfrage kommt, sobald eine Wahl darin steht. */
  async function abbrechen(): Promise<void> {
    if (speichert) return;
    if (!(await darfAbbrechen(gewaehlt !== null))) return;
    p.onAbbrechen();
  }

  /*
   * Der Hinweis VOR dem Speichern (Ruling vom 12.09.2026): Der Server verhindert nichts
   * mehr — er ersetzt eine bestehende Merkzeile und sagt, wessen. Der Schutz vor dem
   * VERSEHEN gehört damit hierher, in die Maske, und zwar vor die Tat.
   *
   * Die Grundlage ist `zugeordneterKunde` der Gruppe (der Stand VOR der Tat). `null` heisst
   * dreierlei — nichts zugeordnet, mehrere verschiedene Kunden, oder der Kunde ist für
   * diesen Benutzer unsichtbar; in allen drei Fällen gibt es hier nichts Ehrliches zu
   * nennen, und dann sagt die Quittung HINTERHER, was ersetzt wurde.
   */
  const bisher = gruppe.zugeordneterKunde;
  const ersetzt = bisher !== null && gewaehlt !== null && gewaehlt.customerId !== bisher.customerId
    ? (merken
      ? `Bisher gemerkt für ${bisher.name} — wird ersetzt.`
      : `Bisher zugeordnet: ${bisher.name} — die Zuordnung wird ersetzt.`)
    : null;

  async function speichern(): Promise<void> {
    if (gewaehlt === null || speichert) return;
    const quittung = await formular.aktion();
    if (quittung !== undefined) p.onFertig(quittung);
  }

  return (
    <Modal titel={`Nummer ${gruppe.rawNumber} zuordnen`} breite="breit"
      abbrechenGesperrt={speichert} onAbbrechen={abbrechen}>
      <div className="ac__panel">
        <section className="ac__panel-teil" aria-label="Anrufe dieser Nummer">
          <h5 className="ac__panel-titel">
            Anrufe dieser Nummer ({gruppe.anzahl})
          </h5>
          <ul className="ac__anrufe">
            {gruppe.anrufe.map((a, i) => (
              <li key={`${a.startedAt}-${i}`} className="ac__anruf">
                <span className="ac__anruf-zeit">{formatZeitpunkt(a.startedAt)}</span>
                <span>{RICHTUNG[a.direction]}</span>
                <span>Gespräch {formatDauer(a.talkSeconds)}</span>
                <span>Wartezeit {formatDauer(a.waitSeconds)}</span>
                <span>{a.agentName ?? "—"}</span>
                <span className="ac__marke">{ANRUF_STAND[a.matchStatus]}</span>
              </li>
            ))}
          </ul>
          {gruppe.anzahl > gruppe.anrufe.length && (
            <p className="ac__zusatz">
              Gezeigt sind die {gruppe.anrufe.length} jüngsten Anrufe. Die Zuordnung gilt
              für alle {gruppe.anzahl}.
            </p>
          )}
        </section>

        <section className="ac__panel-teil" aria-label="Kunde wählen">
          <h5 className="ac__panel-titel">Kunde wählen</h5>
          {gruppe.kandidaten.length > 0 && (
            <div className="ac__vorschlaege">
              <span className="ac__zusatz">
                {gruppe.mehrdeutig
                  ? "Mehrere Kunden tragen diese Nummer — einer davon:"
                  : "Vorschlag aus den Stammdaten:"}
              </span>
              {gruppe.kandidaten.map((k) => (
                <KiKnopf key={k.customerId} gedrueckt={gewaehlt?.customerId === k.customerId} onClick={() => setGewaehlt(k)}>
                  {k.name}
                </KiKnopf>
              ))}
            </div>
          )}
          {/*
            * Die Kundenliste des Hauses statt einer eigenen Suchmaske: dieselbe Verzögerung,
            * dieselben Kürzel-Chips, derselbe Satz „Kein Kunde gefunden“. Ihre Auswahl wird
            * hier auf den Kandidaten-Typ der Klärliste umgesetzt — das ist der Typ, den der
            * Schreibweg erwartet.
            */}
          <KundenListe
            items={treffer}
            loading={laedt}
            error={suchFehler}
            search={suche}
            onSearch={setSuche}
            selectedId={gewaehlt?.customerId ?? null}
            onSelect={(id) => {
              const kunde = treffer.find((k) => k.id === id);
              if (kunde !== undefined) {
                setGewaehlt({ customerId: kunde.id, name: kunde.companyName });
              }
            }}
            {...(p.companies === undefined ? {} : { companies: p.companies })}
          />
        </section>

        <section className="ac__panel-teil" aria-label="Dauerhaft merken">
          {/*
            * Der Schalter fehlt, wo er nicht gehen KANN: Ohne Rufnummer nach E.164 gibt es
            * keinen Abgleichsschlüssel, den man sich merken könnte (der Fehler
            * NUMMER_NICHT_MERKBAR ist nur der Riegel dahinter). Ein Schalter, der
            * zuverlässig mit 422 endet, ist kein Angebot.
            */}
          {gruppe.normalizedNumber !== null ? (
            /* Echte Ja/Nein-Angabe → `Schalter` (me-schalter) im `Formular`, das ihn
               während des Zuordnens sperrt (ME-Etappe 3, Aufgabe 7). */
            <Formular beschriftung="oben" {...formular.formularProps}>
              <Schalter id="ac-merken" beschriftung="Diese Nummer dauerhaft merken"
                an={merken} onGeaendert={(d) => setMerken(d.an)} />
            </Formular>
          ) : (
            <p className="ac__zusatz">
              Diese Nummer lässt sich nicht dauerhaft merken — sie ist keine Rufnummer nach
              E.164 (unterdrückt oder unvollständig). Die Zuordnung der vorhandenen Anrufe
              geht trotzdem.
            </p>
          )}
          {ersetzt !== null && <Hinweis art="warnung">{ersetzt}</Hinweis>}
        </section>

        {formular.meldung !== null && <Hinweis art="fehler">{formular.meldung}</Hinweis>}
      </div>

      <ModalFuss
        speichernText="Zuordnen"
        speichernLabel={`Nummer ${gruppe.rawNumber} dem gewählten Kunden zuordnen`}
        speichernGesperrt={gewaehlt === null || speichert}
        abbrechenGesperrt={speichert}
        onSpeichern={() => { void speichern(); }}
        onAbbrechen={abbrechen}
      />
    </Modal>
  );
}

/**
 * Die Quittung nach dem Zuordnen — samt der ERSETZTEN Merkzeile, wenn es eine gab.
 *
 * `name: null` heisst „ein Kunde, den dieser Benutzer nicht sehen darf“. Dann wird das
 * neutral gesagt und nicht verschwiegen: Dass etwas ersetzt wurde, ist die Auskunft; WER
 * es war, ist die, die der Sichtbarkeit unterliegt.
 */
export function quittungsText(
  rawNumber: string, kunde: string,
  ergebnis: { anrufe: number; gemerkt: boolean;
              ersetzteZuordnung: { name: string | null } | null },
): string {
  const teile = [
    `Nummer ${rawNumber} → ${kunde}: ${ergebnis.anrufe} `
    + `${ergebnis.anrufe === 1 ? "Anruf" : "Anrufe"} zugeordnet.`,
  ];
  if (ergebnis.gemerkt) teile.push("Die Nummer ist jetzt dauerhaft gemerkt.");
  if (ergebnis.ersetzteZuordnung !== null) {
    teile.push(ergebnis.ersetzteZuordnung.name === null
      ? "Die bisherige Merkzeile wurde ersetzt — der bisher gemerkte Kunde ist für dich "
        + "nicht sichtbar."
      : `Die bisherige Merkzeile (${ergebnis.ersetzteZuordnung.name}) wurde ersetzt.`);
  }
  return teile.join(" ");
}

/**
 * Die Fehlerbilder des Schreibwegs in Klartext.
 *
 * Die 404 ist mit Absicht nicht unterscheidbar („die Nummer gibt es hier nicht“ ODER „der
 * Kunde ist unbekannt bzw. für dich unsichtbar") — die Meldung nennt deshalb beide
 * Möglichkeiten, statt eine davon zu behaupten. Die 422 kann es nach den Regeln dieser
 * Maske gar nicht geben (der Schalter fehlt dann); sie steht hier trotzdem, weil ein
 * stiller Fehlschlag das Schlimmere wäre.
 */
function zuordnenFehler(e: unknown): string {
  const status = typeof e === "object" && e !== null
    ? (e as { status?: number }).status : undefined;
  if (status === 404) {
    return "Diese Nummer oder dieser Kunde ist hier nicht (mehr) zu finden. Bitte die "
      + "Liste noch einmal laden.";
  }
  if (status === 422) {
    return "Diese Nummer lässt sich nicht dauerhaft merken — es ist nichts geschehen. "
      + "Ohne den Schalter geht die Zuordnung.";
  }
  return "Die Zuordnung ist nicht durchgekommen. Bitte noch einmal versuchen.";
}
